import CreateCampaignDataForm from "../create/_components/forms/CreateCampaignDataForm";
import { useGetCampaignById } from "@/lib/api/engageApi";
import { useParams, Link } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/_components/button";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/_components/alert";
import { Skeleton } from "@/components/_components/skeleton";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import { ErrorScreen } from "@/components/_components/error-screen";
import { Card, CardContent } from "@/components/_components/card";
import { useAuthorities } from "@/hooks/useAuthorities";

// Define schema for route params
const campaignIdSchema = z.object({
  id: z
    .string()
    .regex(/^\d+$/, "Campaign ID must be a valid number")
    .transform((val) => Number.parseInt(val, 10)),
});

const ViewCampaignPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_CAMPAIGN_MGMT_VIEW">
      <ViewCampaignContent />
    </PageAccessGuard>
  );
};

const ViewCampaignContent: React.FC = () => {
  const params = useParams<{ id: string }>();
  const { hasAuthority } = useAuthorities();

  const canUpdate = hasAuthority('ADMIN_CAMPAIGN_MGMT_UPDATE');

  // Validate using zod
  const parseResult = campaignIdSchema.safeParse(params);

  const campaignId = parseResult?.data?.id ?? null;

  const { data, isLoading, error } = useGetCampaignById(campaignId as number, {
    query: {
      enabled: parseResult.success,
      refetchOnMount: 'always',
      staleTime: 0,
    },
    request: {
      method: "GET",
    },
  });

  if (!parseResult.success) {
    return (
      <div className="bg-background p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive" className="shadow-sm bg-card border-border">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-foreground">
              Invalid campaign ID. Please check the URL and try again.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="bg-background">
        <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-2 py-4">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="flex items-center gap-4 mb-4">
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-6 w-px" />
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div>
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </div>
          </div>
          {/* Form Skeleton */}
          <Card className="shadow-md border-border bg-card">
            <CardContent className="p-6">
              <Skeleton className="h-64 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorScreen
        error={error}
        title="Failed to Load Campaign"
        backLink="/campaign-management"
        backLabel="Back to Campaigns"
      />
    );
  }

  return (
    <div className="bg-background">
      {/* Sticky Header - Matching Create Campaign */}
      <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center space-x-4">
              <Link to="/campaign-management">
                <Button variant="ghost" className="text-base text-muted-foreground hover:bg-accent transition-colors">
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back to Campaigns
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content - Matching Create Campaign */}
      <div className="max-w-7xl mx-auto px-2 sm:px-2 lg:px-4 py-4">
        <CreateCampaignDataForm data={data?.data} readOnly={!canUpdate} />
      </div>
    </div>
  );
};

export default ViewCampaignPage;